<template>
    <div>
        <div class="pg-head">
            <div class="pg-ico"><span class="mdi mdi-calendar-plus"></span></div>
            <div>
                <div class="id-h1">{{ $t('reservation.title') }}</div>
                <div class="pg-sub">{{ $t('reservation.subtitle') }}</div>
            </div>
        </div>

        <!-- always-on reminder: book ≥1 day ahead so coaches can plan; same-day open until 08:00 -->
        <div class="reserve-notice">
            <span class="mdi mdi-bell-ring-outline"></span>
            <div>
                <div>{{ $t('reservation.advanceNotice') }}</div>
                <div class="t-cap">{{ $t('reservation.advanceNoticeCutoff') }}</div>
            </div>
        </div>

        <!-- โหมดการจอง: วันเดียว (flow เดิม) / หลายวัน (เลือกหลายวัน + รอบเวลารายวัน) -->
        <div class="reserve-modes">
            <button type="button" class="reserve-mode-btn" :class="{ on: mode === 'single' }" @click="setMode('single')">
                <span class="mdi mdi-calendar"></span> {{ $t('reservation.modeSingle') }}
            </button>
            <button type="button" class="reserve-mode-btn" :class="{ on: mode === 'multi' }" @click="setMode('multi')">
                <span class="mdi mdi-calendar-multiselect"></span> {{ $t('reservation.modeMulti') }}
            </button>
        </div>

        <v-form ref="reserveForm">
            <div class="grid2 grid2-booking">
                <!-- STEP 1 — pick a date (single) / dates (multi) -->
                <div class="scard" style="padding:18px;max-width:340px;width:100%;align-self:start">
                    <div class="step-head"><span class="step-num">1</span> {{ $t('common.selectDate') }}</div>
                    <template v-if="mode === 'multi'">
                        <div class="t-cap" style="margin-bottom:8px">{{ $t('reservation.multiHint') }}</div>
                        <id-calendar
                            multi
                            :selected-keys="multiDates"
                            :holiday-keys="holidays.map(h => h.year() + '-' + (h.month() + 1) + '-' + h.date())"
                            :holidays="holidayDetails"
                            :min-date="minBookDate"
                            :past-reason="$t('calendar.tooltipBookingClosed')"
                            @toggle-date="toggleMultiDate">
                        </id-calendar>
                        <div class="md-count" :class="{ 'is-full': multiDates.length >= maxMultiDays }">
                            <span class="mdi mdi-calendar-check"></span>
                            {{ $t('reservation.multiCount', { n: multiDates.length, max: maxMultiDays }) }}
                        </div>
                    </template>
                    <id-calendar v-else
                        :model-value="date ? (date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate()) : ''"
                        :holiday-keys="holidays.map(h => h.year() + '-' + (h.month() + 1) + '-' + h.date())"
                        :holidays="holidayDetails"
                        :min-date="minBookDate"
                        :past-reason="$t('calendar.tooltipBookingClosed')"
                        @update:model-value="key => { const p = key.split('-'); date = new Date(+p[0], +p[1] - 1, +p[2]); selectDate(); }">
                    </id-calendar>
                </div>

                <!-- STEP 2 — pick a time slot (single) / a slot per day (multi) -->
                <div class="scard" style="padding:18px;align-self:start">
                    <div class="step-head" :class="{ 'is-muted': mode === 'multi' ? !multiDates.length : !date }"><span class="step-num">2</span> {{ mode === 'multi' ? $t('reservation.stepTimeMulti') : $t('reservation.stepTime') }}</div>

                    <div class="reserve-datebar" style="margin-top:-2px">
                        <span class="mdi mdi-account-child-circle"></span>
                        <b>{{ $t('reservation.bookingFor', { name: student.firstname + ' ' + student.lastname }) }}</b>
                    </div>

                    <!-- โหมดหลายวัน: การ์ดรายวัน — แต่ละวันมีรอบเวลาของวันนั้นให้เลือก (เวลาไม่จำเป็นต้องตรงกัน) -->
                    <template v-if="mode === 'multi'">
                        <div v-if="!multiDates.length" class="reserve-empty">
                            <span class="mdi mdi-calendar-cursor"></span>
                            <div>{{ $t('reservation.pickDatesFirst') }}</div>
                        </div>
                        <div v-else>
                            <div v-for="d in multiDayList" :key="d.key" class="md-day">
                                <div class="md-day-head">
                                    <span class="mdi mdi-calendar" style="color:var(--c-primary)"></span>
                                    <b>{{ format_date(d.date) }}</b>
                                    <div style="flex:1"></div>
                                    <span v-if="d.sel" class="badge badge-success">{{ d.sel.classtime }}</span>
                                    <span v-else-if="!d.loading && d.slots && d.slots.length" class="badge badge-warning">{{ $t('reservation.pickSlot') }}</span>
                                    <button type="button" class="md-day-x" v-hint="$t('reservation.removeDay')" @click="toggleMultiDate(d.key)">
                                        <span class="mdi mdi-close"></span>
                                    </button>
                                </div>
                                <div v-if="d.loading" class="id-skel" style="height:38px"></div>
                                <div v-else-if="!d.slots || !d.slots.length" class="t-cap">{{ $t('reservation.noClassTime') }}</div>
                                <div v-else class="md-chips">
                                    <button v-for="s in d.slots" :key="s.classid" type="button" class="md-chip"
                                        :class="{ on: d.sel && d.sel.classid === s.classid }"
                                        :disabled="s.available <= 0 || s.disabled === 1"
                                        @click="selectMultiSlot(d.key, s)">
                                        <span class="mdi" :class="s.disabled ? 'mdi-calendar-lock' : (d.sel && d.sel.classid === s.classid ? 'mdi-check-circle' : 'mdi-clock-outline')"></span>
                                        {{ s.classtime }} · {{ s.disabled ? $t('reservation.unavailable') : (s.available <= 0 ? $t('reservation.full') : $t('reservation.available', { n: s.available })) }}
                                    </button>
                                </div>
                            </div>
                            <!-- สรุปโควต้า + ปุ่มยืนยัน -->
                            <div class="md-summary">
                                <span class="mdi mdi-clipboard-text-clock"></span>
                                <span>
                                    {{ $t('reservation.multiSummary', { n: multiDates.length }) }}
                                    <template v-if="student.coursetype !== 'Monthly'"> · {{ $t('reservation.multiSummaryUse', { n: multiDates.length, a: student.remaining, b: student.remaining - multiDates.length }) }}</template>
                                </span>
                            </div>
                            <button type="button" class="id-btn id-btn-primary" style="width:100%;margin-top:10px"
                                :disabled="!allSlotsChosen"
                                @click="openMultiConfirm">
                                <span class="mdi mdi-star-shooting"></span> {{ $t('reservation.multiBookBtn', { n: multiDates.length }) }}
                            </button>
                        </div>
                    </template>

                    <div v-else-if="!date" class="reserve-empty">
                        <span class="mdi mdi-calendar-cursor"></span>
                        <div v-html="$t('reservation.pickDateFirst')"></div>
                    </div>
                    <div v-else>
                        <div class="reserve-datebar">
                            <span class="mdi mdi-calendar-check"></span> {{ $t('reservation.slotsOf') }} <b>{{ format_date(date) }}</b>
                            <v-progress-circular v-if="loadingSlots" indeterminate size="16" width="2" color="primary" style="margin-left:8px"></v-progress-circular>
                        </div>
                        <!-- loading: skeleton placeholders so the user sees data being re-fetched -->
                        <div v-if="loadingSlots" class="cards3" style="grid-template-columns:1fr">
                            <div v-for="i in 2" :key="'sk' + i" class="scard" style="padding:16px">
                                <div class="id-skel" style="height:22px;width:52%;margin-bottom:14px"></div>
                                <div class="id-skel" style="height:13px;width:38%;margin-bottom:16px"></div>
                                <div class="id-skel" style="height:42px"></div>
                            </div>
                        </div>
                        <div v-else-if="!classtimesData.length" class="reserve-empty">
                            <span class="mdi mdi-clock-alert-outline"></span>
                            <div>{{ $t('reservation.noClassTime') }}</div>
                        </div>
                        <div v-else class="cards3 id-fade-in" :key="SQLDate(date)" style="grid-template-columns:1fr">
                            <div v-for="s in classtimesData" :key="s.classid" class="scard" style="padding:16px">
                                <div class="row" style="gap:8px;margin-bottom:8px">
                                    <span class="mdi mdi-clock-outline" style="color:var(--c-primary);font-size:20px"></span>
                                    <span class="strong" style="font-size:18px">{{ s.classtime }}</span>
                                    <div style="flex:1"></div>
                                    <span class="badge" :class="s.available <= 0 ? 'badge-error' : 'badge-success'">{{ s.disabled ? $t('reservation.unavailable') : (s.available <= 0 ? $t('reservation.full') : $t('reservation.available', { n: s.available })) }}</span>
                                </div>
                                <div class="t-cap" style="margin-bottom:14px">{{ s.disabled ? ($t('reservation.unavailable') + (s.description ? ' · ' + s.description : '')) : s.text }}</div>
                                <button type="button" class="id-btn" :class="s.available <= 0 ? 'id-btn-ghost' : 'id-btn-primary'"
                                    style="width:100%" :disabled="s.available <= 0"
                                    @click="classtimeSelect = s; validate()">
                                    <span class="mdi" :class="s.disabled ? 'mdi-calendar-lock' : (s.available <= 0 ? 'mdi-calendar-remove' : 'mdi-star-shooting')"></span>
                                    {{ s.disabled ? $t('reservation.unavailable') : (s.available <= 0 ? $t('reservation.full') : $t('reservation.bookThisSlot')) }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-form>

        <id-modal v-model="questionDialog" size="sm" icon="mdi-calendar-check" :title="$t('reservation.title')" persistent>
            <p style="margin:0;text-align:center">{{ $t('reservation.confirmBooking', { name: student.nickname, date: format_date(date), time: classtimeSelect.classtime }) }}</p>
            <!-- blind: hide this child's name from OTHER parents on the View Classes roster -->
            <div class="reserve-blind" @click="blindName = !blindName">
                <span class="idsw" :class="{ on: blindName }"><span class="track"></span><span class="thumb"></span></span>
                <div>
                    <div class="strong">{{ $t('reservation.blindName') }}</div>
                    <div class="t-cap">{{ $t('reservation.blindNameHint') }}</div>
                </div>
            </div>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="questionDialog = false">{{ $t('btn.cancel') }}</button>
                <button class="id-btn id-btn-primary" :disabled="submitting" @click="submitReservation">
                    <span class="mdi mdi-check"></span> {{ $t('reservation.confirmBtn') }}</button>
            </template>
        </id-modal>

        <!-- ยืนยันการจองหลายวัน (all-or-nothing) -->
        <id-modal v-model="multiConfirmDialog" size="md" icon="mdi-calendar-multiple-check" :title="$t('reservation.multiConfirmTitle', { n: multiDates.length })" persistent>
            <div class="t-cap" style="margin-bottom:10px">{{ $t('reservation.bookingFor', { name: student.nickname }) }}</div>
            <div v-for="d in multiDayList" :key="'cf' + d.key" class="md-confirm-row">
                <span class="strong" style="flex:1">{{ format_date(d.date) }}</span>
                <span class="badge badge-neutral">{{ d.sel ? d.sel.classtime : '' }}</span>
            </div>
            <div v-if="student.coursetype !== 'Monthly'" class="t-cap" style="margin-top:10px">
                {{ $t('reservation.multiSummaryUse', { n: multiDates.length, a: student.remaining, b: student.remaining - multiDates.length }) }}
            </div>
            <div class="reserve-blind" @click="blindName = !blindName">
                <span class="idsw" :class="{ on: blindName }"><span class="track"></span><span class="thumb"></span></span>
                <div>
                    <div class="strong">{{ $t('reservation.blindName') }}</div>
                    <div class="t-cap">{{ $t('reservation.blindNameHint') }}</div>
                </div>
            </div>
            <div class="t-cap" style="margin-top:12px">{{ $t('reservation.multiAllOrNothing') }}</div>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="multiConfirmDialog = false">{{ $t('btn.cancel') }}</button>
                <button class="id-btn id-btn-primary" :disabled="submitting" @click="submitMultiDayBooking">
                    <span class="mdi mdi-check"></span> {{ $t('reservation.confirmBtn') }}</button>
            </template>
        </id-modal>
    </div>

</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex';
import { formatFullDate, toDate } from '@/utils/locale';
export default {
    props: {
        student: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            date: null,
            minDate: null,
            previousMonth: new Date().getMonth(),
            previousYear: new Date().getFullYear(),
            classtimeSelect: null,
            classtimesData: [],
            loadingSlots: false,
            // ล็อกปุ่มยืนยันระหว่างส่งคำขอ — เดิมพึ่ง loading overlay กันแบบอ้อม ๆ อย่างเดียว
            // ถ้าเน็ตช้าแล้วผู้ใช้กดซ้ำ จะยิงจองซ้อนกันหลายใบ (backend มี checkDuplicate กันอีกชั้น
            // แต่การจองพร้อมกันบนครั้งสุดท้ายก็ยังเป็น race ที่ไม่ควรปล่อยให้เกิดตั้งแต่ต้นทาง)
            submitting: false,
            people: '',
            classTimeRules: [
                v => !!v || this.$t('common.required'),
                v => !v || (v.available > 0) || this.$t('home.errorNoRemaining')
            ],
            //weekday:['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
            questionDialog: false,
            holidays: [],
            holidayDetails: [],
            blindName: false,
            // ── โหมดจองหลายวัน ──
            mode: 'single',          // 'single' = flow เดิม, 'multi' = เลือกหลายวัน + รอบเวลารายวัน
            multiDates: [],          // calendar keys 'YYYY-M-D' ของวันที่เลือก (ลำดับตามการกด — แสดงผลเรียงวันที่ผ่าน multiDayList)
            dayData: {},             // key -> { slots: [], sel: slot|null, loading: bool } — รอบเวลาของแต่ละวัน
            multiConfirmDialog: false,
        }
    },
    async created() {
        await this.fetchHolidays();
        this.date = new Date();
        this.date.setDate(this.date.getDate() + 1);
        this.minDate = new Date();        
        await this.getHolidayInformation();
        await this.getClassTime();
    },
    methods: {
        async selectDate() {
            this.classtimeSelect = null;
            await this.getClassTime();
        },
        // ── โหมดจองหลายวัน ──
        parseKey(k) { const p = k.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); },
        setMode(m) {
            if (this.mode === m) return;
            this.mode = m;
            // เคลียร์สถานะของอีกโหมด กันค่าค้างข้ามโหมด
            this.classtimeSelect = null;
            this.multiDates = [];
            this.dayData = {};
            this.blindName = false;
        },
        async toggleMultiDate(key) {
            const i = this.multiDates.indexOf(key);
            if (i >= 0) {
                this.multiDates.splice(i, 1);
                delete this.dayData[key];
                return;
            }
            if (this.multiDates.length >= this.maxMultiDays) {
                this.$emit('onErrorHandler', this.$t('reservation.multiMax', { n: this.maxMultiDays }));
                return;
            }
            this.multiDates.push(key);
            await this.fetchDaySlots(key);
        },
        // โหลดรอบเวลาของวันเดียว (endpoint /getClassTime เดิม) — เก็บลง dayData[key]
        async fetchDaySlots(key) {
            const date = this.parseKey(key);
            this.dayData[key] = { slots: [], sel: null, loading: true };
            const t0 = Date.now();
            const token = this.$store.getters.getToken;
            let slots = [];
            try {
                const response = await axios.post(this.baseURL + '/getClassTime', {
                    classdate: this.SQLDate(date),
                    classday: date.toLocaleDateString('en-US', { weekday: 'long' }),
                    courseid: this.student.courseid
                }, { headers: { Authorization: `Bearer ${token}` } });
                slots = (response.data.success && response.data.results.length) ? response.data.results : [];
            } catch (error) {
                this.$emit('onErrorHandler', error.message);
            }
            await this.$minLoad(t0);
            if (!this.dayData[key]) return; // วันถูกเอาออกระหว่างโหลด
            // มีรอบที่จองได้อยู่รอบเดียว → เลือกให้อัตโนมัติ
            const bookable = slots.filter(s => s.available > 0 && !s.disabled);
            this.dayData[key] = { slots, sel: bookable.length === 1 ? bookable[0] : null, loading: false };
        },
        selectMultiSlot(key, s) {
            if (s.available <= 0 || s.disabled) return;
            const d = this.dayData[key];
            if (!d) return;
            d.sel = (d.sel && d.sel.classid === s.classid) ? null : s; // กดซ้ำ = เอาออก
        },
        openMultiConfirm() {
            if (!this.allSlotsChosen) {
                this.$emit('onErrorHandler', this.$t('reservation.multiPickAll'));
                return;
            }
            this.multiConfirmDialog = true;
        },
        async submitMultiDayBooking() {
            if (this.submitting) return;   // กันกดยืนยันซ้ำ
            this.submitting = true;
            this.multiConfirmDialog = false;
            this.$emit('onLoading', true);
            const token = this.$store.getters.getToken;
            try {
                const response = await axios.post(this.baseURL + '/addMultiDayBookingByCustomer', {
                    studentid: this.student.studentid,
                    courseid: this.student.courseid,
                    blindflag: this.blindName ? 1 : 0,
                    items: this.multiDayList.map(d => ({
                        classdate: this.SQLDate(d.date),
                        classid: d.sel.classid,
                        classtime: d.sel.classtime,
                    })),
                }, { headers: { Authorization: `Bearer ${token}` } });
                if (response.data.success) {
                    this.$emit('onInfoHandler', this.$t('reservation.multiSuccess', { n: this.multiDayList.length }));
                    this.$emit('onSuccessHandler', 'home');
                } else {
                    // all-or-nothing: backend บอกเหตุผล (+วันที่ติดถ้ามี) — โหลดรอบของทุกวันใหม่ให้เห็นสถานะล่าสุด
                    this.$emit('onErrorHandler', response.data.message || 'Booking failed');
                    await Promise.all(this.multiDates.map(k => this.fetchDaySlots(k)));
                }
            } catch (error) {
                this.$emit('onErrorHandler', error.message || 'Booking failed');
            } finally {
                this.blindName = false;   // explicit opt-in per booking — reset for the next one
                this.submitting = false;
                this.$emit('onLoading', false);
            }
        },
        handleMonthChange(newDate) {
            //console.log("handleMonthChange", newDate);
            const newMonth = new Date(newDate).getMonth();
            const newYear = new Date(newDate).getFullYear();

            // ตรวจสอบเดือนและปีที่เลือกใหม่
            if (newMonth !== this.previousMonth || newYear !== this.previousYear) {
                // หากเดือนหรือปีเปลี่ยนแปลง ให้เรียก API
                this.getHolidayInformation();
                
                // อัปเดตค่าของเดือนและปี
                this.previousMonth = newMonth;
                this.previousYear = newYear;
            }
        },
        isDateAllowed(date) {
            const isMonday = moment(date).day() === 1;  // ตรวจสอบว่าวันที่นั้นเป็นวันจันทร์หรือไม่
            const isHoliday = this.holidays.some(holiday => moment(date).isSame(holiday, 'day')); // ตรวจสอบว่าวันที่เป็นวันหยุดหรือไม่
            //console.log('Checking date:', moment(date).format('YYYY-MM-DD'), 'isMonday:', isMonday, 'isHoliday:', isHoliday);
            return !isMonday && !isHoliday;
        },
        async fetchHolidays() {
            try {
                const token = this.$store.getters.getToken;
                const response = await axios.get(this.baseURL + '/holidaysList', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (response.data.success) {
                    this.holidays = response.data.holidays.map(date => moment(date).startOf('day'));
                    this.holidayDetails = response.data.details || [];
                } else {
                    this.$emit('onErrorHandler', response.data.message);
                }
            } catch (error) {
                this.$emit('onErrorHandler', error.message);
            }
        },
        async validate() {
            let { valid } = await this.$refs.reserveForm.validate()
            if (!valid) return;
            // hard guard: customers cannot book past the deadline (past dates, or today
            // after 08:00) — calendar already disables those, but block here too in
            // case date is set another way; backend re-checks with Bangkok time anyway
            if (!this.date || this.date < this.minBookDate) {
                this.$emit('onErrorHandler', this.$t('reservation.bookCutoff'))
                return;
            }
            if (this.people.remaining <= 0) {
                this.$emit('onErrorHandler', this.$t('msg.noRemaining'))
                return;
            }
            this.questionDialog = true
            //await this.submitReservation()
        },
        async getClassTime() {
            const req = {
                classdate: this.SQLDate(this.date),
                classday: toDate(this.date).toLocaleDateString('en-US', { weekday: 'long' }),
                courseid: this.student.courseid
            };

            this.loadingSlots = true;
            const t0 = Date.now();
            const token = this.$store.getters.getToken;
            try {
                const response = await axios.post(this.baseURL + '/getClassTime', req, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    this.classtimesData = response.data.results.length ? response.data.results : [];
                } else {
                    this.classtimesData = [];
                }
            } catch (error) {
                this.$emit('onErrorHandler', error.message);
            } finally {
                await this.$minLoad(t0);
                this.loadingSlots = false;
            }
        },
        async getHolidayInformation() {
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/getHolidayInformation', {
                selectdate: this.SQLDate(this.date),
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                })
                .then(response => {
                    console.dir(response);
                    
                })
        },
        getNextAvailableDate(date) {
            let nextDate = new Date(date);
            nextDate.setDate(nextDate.getDate() + 1); // เริ่มจากวันพรุ่งนี้
            while (this.isHoliday(nextDate) || moment(nextDate).day() === 1) { // ตรวจสอบวันหยุดและวันจันทร์
                nextDate.setDate(nextDate.getDate() + 1);
            }
            return nextDate;
        },
        isHoliday(date) {
            return this.holidays.some(holiday => moment(date).isSame(holiday, 'day'));
        },
        async submitReservation() {
            if (this.submitting) return;   // กันกดยืนยันซ้ำ
            this.submitting = true;
            this.$emit('onLoading', true)
            this.questionDialog = false;
            let isDuplicate = false;
            const reservaObj = {
                studentid: this.student.studentid,
                classdate: this.SQLDate(this.date),
                classtime: this.classtimeSelect.classtime,
                classid: this.classtimeSelect.classid,
                courseid: this.student.courseid,
                classday: toDate(this.date).toLocaleDateString('en-US', { weekday: 'long' }),
                studentname: this.student.firstname + ' ' + this.student.lastname,
                studentnickname: this.student.nickname,
                blindflag: this.blindName ? 1 : 0,
            }
            //console.log('checkDuplicateReservation : ', reservaObj)
            const token = this.$store.getters.getToken;
            // ครอบทั้งก้อนด้วย try/catch/finally และปิด loading ใน finally — เดิม /checkDuplicateReservation
            // มีแค่ .then() ไม่มี .catch() ถ้ามันล้ม (เน็ตหลุด / 500 / 401) promise จะ reject ออกมา
            // ทำให้บรรทัดปิดท้ายไม่ทำงาน = สปินเนอร์ค้างทั้งจอถาวรจนผู้ใช้ต้อง reload และใบจองหายเงียบ
            // (แม่แบบเดียวกับ submitMultiDayBooking ในไฟล์นี้ที่ทำถูกอยู่แล้ว)
            try {
                await axios.post(this.baseURL + '/checkDuplicateReservation', reservaObj, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                    .then(response => {
                        //console.dir(response);
                        if (!response.data.success) {
                            this.$emit('onErrorHandler', this.$t('msg.bookingDuplicate', { date: this.format_date(this.date) }))
                            isDuplicate = true;
                        }
                    });

                if (!isDuplicate) {
                    await axios.post(this.baseURL + '/addBookingByCustomer', reservaObj, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    })
                        .then(response => {
                            //console.dir(response);
                            if (response.data.success) {
                                this.$emit('onInfoHandler', this.$t('msg.bookingSuccess'))
                                this.$emit('onSuccessHandler', 'home')
                            } else {
                                this.$emit('onErrorHandler', response.data.message || this.$t('common.reservationFailed'))
                            }
                        });
                }
            } catch (error) {
                const status = error.response && error.response.status;
                if (status === 401) {
                    this.$emit('onErrorHandler', this.$t('common.sessionExpired'))
                } else {
                    this.$emit('onErrorHandler', (error.response && error.response.data && error.response.data.message) || error.message || this.$t('common.reservationFailed'))
                }
            } finally {
                this.blindName = false   // explicit opt-in per booking — reset for the next one
                this.submitting = false
                this.$emit('onClickChangeState', this.student)
                this.$emit('onLoading', false)
            }
        },
        SQLDate(value) {
            if (value) {
                return moment(value).format('YYYYMMDD')
            }
        },
        format_date(value) {
            // full weekday+date in the app language (Buddhist era for Thai); was navigator.language
            return value ? formatFullDate(value) : '';
        },
        
    },
    watch: {
        date(newDate) {
            if (this.isHoliday(newDate) || moment(newDate).day() === 1) {
                this.date = this.getNextAvailableDate(newDate);
            }
        }
    },
    computed: {
        ...mapGetters({
            token: 'getToken',
        }),
        // customers book from TOMORROW onward — except before 08:00, when today's
        // classes are still bookable (same-day cutoff; backend enforces the same rule)
        minBookDate() {
            const d = new Date();
            if (d.getHours() >= 8) d.setDate(d.getDate() + 1);
            d.setHours(0, 0, 0, 0);
            return d;
        },
        // เพดานจำนวนวันต่อการจองหนึ่งชุด: 10 วัน (ล้อกับเพดานจองกลุ่ม 10 คน)
        // คอร์สนับครั้ง: ห้ามเกินจำนวนครั้งคงเหลือด้วย — Monthly ไม่จำกัดครั้ง ใช้ 10 ตรงๆ
        maxMultiDays() {
            const cap = 10;
            if (this.student && this.student.coursetype !== 'Monthly') {
                const r = parseInt(this.student.remaining);
                if (!isNaN(r)) return Math.max(0, Math.min(cap, r));
            }
            return cap;
        },
        // วันที่เลือกไว้ + ข้อมูลรอบของวันนั้น เรียงตามวันที่จริง (ไม่ใช่ลำดับการกด)
        multiDayList() {
            return this.multiDates
                .map(k => {
                    const d = this.dayData[k] || { slots: [], sel: null, loading: false };
                    return { key: k, date: this.parseKey(k), slots: d.slots, sel: d.sel, loading: d.loading };
                })
                .sort((a, b) => a.date - b.date);
        },
        allSlotsChosen() {
            return this.multiDates.length > 0 && this.multiDayList.every(d => d.sel);
        },
    }

}
</script>

<style scoped>
/* blind toggle row inside the confirm dialog — uses the global .idsw switch */
.reserve-blind {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding: 12px 14px;
    border: 1px solid var(--c-border);
    border-radius: var(--radius-md);
    background: var(--c-surface-2);
    cursor: pointer;
}
.reserve-blind .strong { font-size: 14px; color: var(--c-text-heading); }
.reserve-blind .t-cap { font-size: 12px; }

/* ── โหมดจองหลายวัน ── */
/* สวิตช์เลือกโหมด (segmented) */
.reserve-modes {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
}
.reserve-mode-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 18px;
    border: 1.5px solid var(--c-border-strong);
    border-radius: var(--radius-md);
    background: var(--c-surface);
    color: var(--c-text-body);
    font-family: var(--font-body);
    font-size: 14px;
    cursor: pointer;
}
.reserve-mode-btn.on {
    border-color: var(--c-primary);
    background: var(--c-primary-soft, var(--c-surface-2));
    color: var(--c-text-heading);
    font-weight: 700;
}
.reserve-mode-btn .mdi { color: var(--c-primary); }

/* จำนวนวันที่เลือก (ใต้ปฏิทิน) */
.md-count {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    font-size: 13px;
    color: var(--c-text-body);
}
.md-count .mdi { color: var(--c-primary); }
.md-count.is-full { color: var(--c-warning, var(--c-text-heading)); }

/* การ์ดรายวัน + chips รอบเวลาของวันนั้น */
.md-day {
    border: 1.5px solid var(--c-border);
    border-radius: var(--radius-md);
    padding: 12px 14px;
    margin-bottom: 10px;
    background: var(--c-surface);
}
.md-day-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}
.md-day-head b { color: var(--c-text-heading); font-size: 14px; }
.md-day-x {
    border: 0;
    background: transparent;
    cursor: pointer;
    color: var(--c-text-muted);
    font-size: 17px;
    padding: 2px;
    line-height: 1;
}
.md-day-x:hover { color: var(--c-error); }
.md-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.md-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1.5px solid var(--c-border-strong);
    border-radius: 999px;
    background: var(--c-surface);
    color: var(--c-text-body);
    font-family: var(--font-body);
    font-size: 13px;
    cursor: pointer;
}
.md-chip .mdi { color: var(--c-primary); }
.md-chip.on {
    border-color: var(--c-primary);
    background: var(--c-primary-soft, var(--c-surface-2));
    color: var(--c-text-heading);
    font-weight: 700;
}
.md-chip:disabled {
    opacity: .55;
    cursor: not-allowed;
    background: var(--c-surface-2);
}
.md-chip:disabled .mdi { color: var(--c-text-muted); }

/* แถบสรุปโควต้า */
.md-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    padding: 10px 14px;
    border-radius: var(--radius-md);
    background: var(--c-surface-2);
    color: var(--c-text-heading);
    font-size: 13.5px;
}
.md-summary .mdi { color: var(--c-primary); }

/* แถวสรุปใน modal ยืนยัน */
.md-confirm-row {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--c-border);
    border-radius: var(--radius-md);
    padding: 9px 12px;
    margin-bottom: 8px;
    background: var(--c-surface-2);
}
</style>